import { useMemo } from 'react'
import * as React from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'
import SlideBase from '../SlideBase'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import Group from 'components/utils/Group'
import type { LoyaltyData } from '../../../types/wrapped'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import Label from 'LuxKit/Label'
import { getImageUrl } from 'lib/image/imageUtils'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useWrappedStoryState } from 'contexts/Wrapped'
import InteractiveZone from '../../InteractiveZone'
import TextButton from 'LuxKit/Button/TextButton'
import LELogoSvg from './LELogoSvg'
import useLoyaltyConfig from 'loyalty/hooks/useLoyaltyConfig'
import { EmptyArray } from 'lib/array/arrayUtils'

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
`

type LoyaltyTier = LoyaltyData['tier']

const TIER_IMAGE_IDS: Record<LoyaltyTier, string> = {
  bronze: 'societe-coint-bronze',
  silver: 'societe-coin-silver',
  gold: 'societe-coin-gold',
  platinum: 'societe-coin-platinum',
}

const TIER_DISPLAY_NAMES: Record<LoyaltyTier, string> = {
  bronze: 'Bronze',
  silver: 'Silver',
  gold: 'Gold',
  platinum: 'Platinum',
}

/**
 * Converts a display name (e.g. 'Platinum') to a tier key (e.g. 'platinum')
 */
function displayNameToTierKey(displayName: string): App.LoyaltyTier | undefined {
  const lowerName = displayName.toLowerCase()
  if (lowerName === 'bronze' || lowerName === 'silver' || lowerName === 'gold' || lowerName === 'platinum') {
    return lowerName as App.LoyaltyTier
  }
  return undefined
}

/**
 * Gets the target tier for benefits display based on tier progression
 */
function getTargetTier(
  currentTier: App.LoyaltyTier,
  tierProgression: LoyaltyData['tierProgression'],
): App.LoyaltyTier {
  if (!tierProgression) return currentTier

  if (tierProgression.type === 'unlock' && tierProgression.name) {
    const nextTier = displayNameToTierKey(tierProgression.name)
    if (nextTier) return nextTier
  }

  // For 'retain' type or if name conversion fails, use current tier
  return currentTier
}

function getBenefitTitle(intl: ReturnType<typeof useIntl>, code: App.LoyaltyBenefitType): string | undefined {
  const titles: Record<App.LoyaltyBenefitType, string> = {
    hotel_upgrade: intl.formatMessage({ id: '88uN3y5LgmDkUy9mRxc3', defaultMessage: 'Free room and package upgrades', description: 'Loyalty benefit title for hotel room upgrades in the year-in-review feature' }),
    airport_transfer: intl.formatMessage({ id: '6OMd2AW4sDNMu0Hf1zxw', defaultMessage: 'Free airport chauffeur service', description: 'Loyalty benefit title for airport chauffeur transfers' }),
    lounge_pass: intl.formatMessage({ id: 'bMxOaSfiH6AACyqfYMkS', defaultMessage: 'Airport lounge passes', description: 'Loyalty benefit title for airport lounge access in the year-in-review feature' }),
    discounted_insurance: intl.formatMessage({ id: 'Hg-xZuW4m3WgZnDGQeeN', defaultMessage: 'Travel protection discount', description: 'Loyalty benefit title for discounted travel insurance' }),
    phone_support: intl.formatMessage({ id: 'eWmMeP3z-0hTC7gteHY6', defaultMessage: 'Priority phone support 24/7', description: 'Loyalty benefit title for priority phone support in the year-in-review feature' }),
    tour_exp_credit: intl.formatMessage({ id: 'pJwX5QG7vluX54BtBTNy', defaultMessage: 'Tour optional extra credits', description: 'Loyalty benefit title for tour experience credits in the year-in-review feature' }),
    travelshoot_credit: intl.formatMessage({ id: 'HjDdCxMO7vzvW3SdkW7A', defaultMessage: 'TravelShoot voucher', description: 'Loyalty benefit title for TravelShoot photo voucher' }),
    early_access: intl.formatMessage({ id: 'J0oVgxOfO9OWgBa74QiW', defaultMessage: 'Early access to exclusive offers', description: 'Loyalty benefit title for early access to offers in the year-in-review feature' }),
    esim_package: intl.formatMessage({ id: 'qXGHzLGeNgvIGCyT2Kga', defaultMessage: 'eSIM data package', description: 'Loyalty benefit title for eSIM data package in the loyalty section' }),
    hidden_access: intl.formatMessage({ id: 'V_XoyW-UDoyBqk_lEiDl', defaultMessage: 'Hidden offers access', description: 'Loyalty benefit title for access to hidden offers in the year-in-review feature' }),
  }
  return titles[code]
}

/**
 * Gets the benefit titles for a given tier from the loyalty config
 */
function getTierBenefitTitles(
  intl: ReturnType<typeof useIntl>,
  config: App.LoyaltyConfig,
  tier: App.LoyaltyTier,
): Array<string> {
  const tierBenefits = config.regionTierBenefits[tier] ?? []
  const enabledBenefits = config.regionEnabledBenefits

  return tierBenefits
    .filter(benefit => enabledBenefits[benefit.code])
    .map(benefit => getBenefitTitle(intl, benefit.code))
    .filter((title): title is string => Boolean(title))
}

const Background = styled.div`
  position: absolute;
  inset: 0;

  &.bronze {
    background:
      linear-gradient(180deg, transparent 0%, ${props => props.theme.palette.product.loyalty.bronze.darkBackground} 50%),
      radial-gradient(126.92% 35.81% at 50% 31.09%, ${props => props.theme.palette.product.loyalty.bronze.normalBackground} 0%, ${props => props.theme.palette.product.loyalty.bronze.darkBackground} 100%);
  }

  &.silver {
    background:
      linear-gradient(180deg, transparent 0%, ${props => props.theme.palette.product.loyalty.silver.darkBackground} 50%),
      radial-gradient(126.92% 35.81% at 50% 31.09%, ${props => props.theme.palette.product.loyalty.silver.normalBackground} 0%, ${props => props.theme.palette.product.loyalty.silver.darkBackground} 100%);
  }

  &.gold {
    background:
      linear-gradient(180deg, transparent 0%, ${props => props.theme.palette.product.loyalty.gold.darkBackground} 50%),
      radial-gradient(126.92% 35.81% at 50% 31.09%, ${props => props.theme.palette.product.loyalty.gold.normalBackground} 0%, ${props => props.theme.palette.product.loyalty.gold.darkBackground} 100%);
  }

  &.platinum {
    background:
      linear-gradient(180deg, transparent 0%, ${props => props.theme.palette.product.loyalty.platinum.darkBackground} 50%),
      radial-gradient(126.92% 35.81% at 50% 31.09%, ${props => props.theme.palette.product.loyalty.platinum.normalBackground} 0%, ${props => props.theme.palette.product.loyalty.platinum.darkBackground} 100%);
  }
`

const LELogoBackground = styled.div`
  position: absolute;
  left: 50%;
  top: 38%;
  transform: translate(-50%, -50%);
  width: 70%;
  min-width: ${rem(100)};
  max-width: ${rem(325)};
  aspect-ratio: 325 / 437;
  pointer-events: none;

  svg {
    width: 100%;
    height: 100%;
  }
`

const Card = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${rem(12)};
  padding: 0 0 ${rem(40)};
  border-radius: ${rem(20)};
  flex: 1;
`

const SocieteLogo = styled.img`
  min-width: ${rem(50)};
  max-width: ${rem(126)};
  height: auto;
`

const CoinContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`

const CoinImage = styled.img`
  width: 35%;
  min-width: ${rem(100)};
  max-width: ${rem(190)};
  height: auto;
  object-fit: contain;
  animation: ${float} 3s ease-in-out infinite;
  /* Enable GPU acceleration for smooth animation */
  will-change: transform;
  transform: translateZ(0);

  ${mediaQueryUp.tablet} {
    width: 50%;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    will-change: auto;
  }
`

const BenefitsCard = styled.div`
  width: 100%;
  padding: ${rem(16)} ${rem(20)};
  background: rgba(255, 255, 255, 0.05);
  border-radius: ${rem(8)};
  border: 1px solid rgba(255, 255, 255, 0.3);
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
`

const CheckIconWrapper = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  color: ${props => props.theme.palette.neutral.default.one};
`

function LoyaltySlide() {
  const intl = useIntl()
  const { data } = useWrappedStoryState()
  const loyalty = data?.loyalty

  // Memoize expensive image URL generation
  const societeLogo = useMemo(() => getImageUrl('societe-logo-white', {
    format: 'png',
    quality: 'best',
    dpr: 2,
    width: 126,
  }), [])

  const coinImage = useMemo(() => {
    if (!loyalty) return ''
    return getImageUrl(TIER_IMAGE_IDS[loyalty.tier], {
      format: 'png',
      width: 190,
    })
  }, [loyalty])

  const [config] = useLoyaltyConfig()

  const { tierProgression } = loyalty ?? {}

  const tierName = useMemo(() => {
    if (!loyalty) return ''
    if (tierProgression?.type === 'unlock' && tierProgression.name) {
      return tierProgression.name
    }
    return TIER_DISPLAY_NAMES[loyalty.tier]
  }, [loyalty, tierProgression])

  const targetTier = useMemo(() => {
    if (!loyalty) return undefined
    return getTargetTier(loyalty.tier, tierProgression)
  }, [loyalty, tierProgression])

  const benefitTitles = useMemo(() => {
    if (!config || !targetTier) return EmptyArray as Array<string>
    return getTierBenefitTitles(intl, config, targetTier)
  }, [intl, config, targetTier])

  if (!data || !loyalty) {
    return null
  }

  const tierProgressionMessage = tierProgression ? (
    tierProgression.type === 'unlock' ? (
      <FormattedMessage
        id="9XhFf9Y7Jk81anNKV8p1"
        defaultMessage="You need {creditsNeeded} status credits and {nightsNeeded} nights to unlock <b>{tierName}</b> to enjoy these benefits:"
        description="Year-in-review loyalty slide message showing credits and nights needed to unlock the next tier"
        values={{
          creditsNeeded: tierProgression.creditsNeeded,
          nightsNeeded: tierProgression.nightsNeeded,
          tierName,
          b: (chunks: React.ReactNode) => <b>{chunks}</b>,
        }}
      />
    ) : (
      <FormattedMessage
        id="VNzEo5CW5vj8QZ73wJuQ"
        defaultMessage="You need {creditsNeeded} status credits and {nightsNeeded} nights to remain <b>{tierName}</b> to enjoy these benefits:"
        description="Year-in-review loyalty slide message showing credits and nights needed to retain the current tier"
        values={{
          creditsNeeded: tierProgression.creditsNeeded,
          nightsNeeded: tierProgression.nightsNeeded,
          tierName,
          b: (chunks: React.ReactNode) => <b>{chunks}</b>,
        }}
      />
    )
  ) : null

  return (
    <SlideBase
      gap={60}
      verticalAlign="space-between"
      background={
        <Background className={loyalty.tier}>
          <LELogoBackground>
            <LELogoSvg />
          </LELogoBackground>
        </Background>
      }
    >
      <Card>
        <SocieteLogo src={societeLogo} alt="Société" loading="lazy" />
        <Caption colour="neutral-one" variant="small" format="uppercase">
          {intl.formatMessage({
            id: 'Rn0FGTGqZIMADVj8jAn5',
            defaultMessage: 'loyalty rewards',
            description: 'Caption for the loyalty rewards statistic slide in the year-end wrapped summary',
          })}
        </Caption>
        <CoinContainer>
          <CoinImage src={coinImage} alt={`${loyalty.tier} tier coin`} loading="lazy" />
        </CoinContainer>
        <Group direction="vertical" gap={8} horizontalAlign="center" style={{ textAlign: 'center' }}>
          <Heading variant="heading5" colour="neutral-one">
            <b>
              {intl.formatMessage({
                id: 'Ame12UepzhoL1VUhz-VW',
                defaultMessage: 'In 2025, you earned a total of',
                description: 'Loyalty slide heading about total earned in the loyalty section',
              })}
            </b>
          </Heading>
          <Heading variant="heading2alt" colour="neutral-one">
            <i>
              {loyalty.points > 0 && (
                <>
                  {intl.formatMessage({
                    id: 'KagJHPbKm4ZWDIOLksEG',
                    defaultMessage: '{points} Points',
                    description: 'Points value display for the loyalty rewards slide in the year-end wrapped summary',
                  }, { points: loyalty.points.toLocaleString() })}
                </>
              )}
              {loyalty.points > 0 && loyalty.statusCredits > 0 && <FormattedMessage
                id="UXxgNvdOuRE5EPv14gXR"
                defaultMessage=" & {br}"
                description="Line break and ampersand separator between loyalty points and status credits"
                values={{ br: () => <br /> }}
              />}
              {loyalty.statusCredits > 0 && (
                <>
                  {intl.formatMessage({
                    id: 'WesgNmbqJKGd6xgXXkO5',
                    defaultMessage: '{statusCredits} Status Credits',
                    description: 'Loyalty slide status credits display in the loyalty program section',
                  }, { statusCredits: loyalty.statusCredits })}
                </>
              )}
            </i>
          </Heading>
        </Group>
        <Label kind="primary" variant="dark">
          {intl.formatMessage({
            id: 'qw5t8OD-WC0DqvcYzpWi',
            defaultMessage: 'Top {percentile}% of our travellers',
            description: 'Loyalty slide percentile label in the loyalty program section',
          }, { percentile: loyalty.percentile })}
        </Label>
      </Card>

      {tierProgression && benefitTitles.length > 0 && (
        <BenefitsCard>
          <BodyText variant="medium" colour="neutral-one">
            {tierProgressionMessage}
          </BodyText>
          <Group direction="vertical" gap={8} wrap="wrap" verticalAlign="start">
            {benefitTitles.slice(0, 3).map((benefitTitle) => (
              <Group key={benefitTitle} direction="horizontal" gap={8} verticalAlign="start">
                <CheckIconWrapper>
                  <LineCheckIcon size="XS" />
                </CheckIconWrapper>
                <BodyText variant="medium" colour="neutral-one">
                  {benefitTitle}
                </BodyText>
              </Group>
            ))}
            <InteractiveZone>
              <TextButton shape="round" fit="full-width" kind="secondary" to="/societe" target="_blank">
                {intl.formatMessage({
                  id: 'TrU3mAziRUB2tAEu4Ayz',
                  defaultMessage: 'Learn more',
                  description: 'Loyalty slide button label to learn more in the loyalty section',
                })}
              </TextButton>
            </InteractiveZone>
          </Group>
        </BenefitsCard>
      )}
    </SlideBase>
  )
}

export default React.memo(LoyaltySlide)
